// Type-only — `@repo/types` isn't a runtime dependency of this app.
import type { ExampleKind } from '@repo/types'

import type { AxiosResponse } from 'axios'
import type { Pagination, Action } from 'store/model'

export enum OwnerGender {
  MALE = 'Male',
  FEMALE = 'Female',
  OTHER = 'Other',
  PREFER_NOT_TO_SAY = 'PreferNotToSay',
}

export enum OwnerSortField {
  NAME = 'name',
  STATUS = 'status',
  CERTIFICATES = 'certificates',
  TRANSFERS = 'transfers',
  SCANS = 'scans',
  REGISTRATION_DATE = 'registrationDate',
  AGE = 'age',
  GENDER = 'gender',
  EMAIL = 'email',
  PHONE = 'phone',
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum OwnerStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum IntegrationSource {
  SHOPIFY = 'SHOPIFY',
  MANUAL = 'MANUAL',
  API = 'API',
}

export type Owner = {
  /**
   * VRG-361 — null/absent = real customer data. `demo`/`intro` mark a seeded row and
   * render the "not real data" badge (`ExampleKindBadge`). Same key name on the
   * product, certificate and owner payloads so one component reads all three.
   */
  example_kind?: ExampleKind | null
  id: string
  name: string
  status: string
  certificates: number
  products_count: number
  transfers: number
  scans: number
  // VERA-914 / VRG-105 (Stage B) — distinct leads referred by this owner.
  leads: number
  registrationDate: string
  age: number
  gender: OwnerGender | null
  email: string
  phone: string | null
  avatar: string | null
  workosUserId: string | null
  integrationSource?: IntegrationSource | null
  shopifyCustomerId?: string | null
  integrationMetadata?: Record<string, any> | null
  lastSyncedAt?: string | null
  archived?: boolean
  archived_at?: string | null
  // VERA-914 (Stage B) — the single-owner GET (`/owners/:id`) returns the raw
  // owner-admin-data shape (first_name/last_name/birthday/etc, not this list
  // row's name/age/status), plus the new Details/Address/Marketing fields. Kept
  // optional/loose here since this `Owner` type is shared with the list shape.
  first_name?: string
  last_name?: string
  username?: string
  birthday?: string | null
  country?: string | null
  // VRG-416 — detail-only transfer metrics. `GET /owners/:id` returns a real
  // directional split (COUNT on transfers.to_owner_id / from_owner_id) plus
  // their sum; the list row returns neither, hence optional. Never synthesize a
  // direction from `transfers` — in + out does not sum to the total, because
  // `from_owner_id` is nullable for admin-issued transfers.
  transfers_in_count?: number
  transfers_out_count?: number
  transfers_count?: number
  address_line?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  address_country?: string | null
  // VERA-914 — geocoding from the AddressField autocomplete (VRG-99 scan map).
  latitude?: number | null
  longitude?: number | null
  place_id?: string | null
  formatted_address?: string | null
  email_marketing_consent?: boolean | null
  sms_marketing_consent?: boolean | null
}

export type Owners = {
  page: number
  size: number
  totalItems: number
  totalPages: number
  items: Owner[]
}

export type GetOwnersPayload = {
  page?: number
  size?: number
  q?: string
  sort?: OwnerSortField
  order?: SortOrder
  status?: OwnerStatus
  productId?: string
  gender?: OwnerGender
  ageMin?: number
  ageMax?: number
  registeredFrom?: string
  registeredTo?: string
  integrationSource?: IntegrationSource
  showArchived?: boolean
}

export type GetOwnersResponse = AxiosResponse<Owners>

// Single owner with certificates types
export type OwnerDataResponse = {
  owner: Owner
  certificates: {
    data: any[] // Using any for certificates since we need to import CertificateAdmin type
  } & Pagination
}

export type GetOwnerByIdPayload = {
  ownerId: string
  limit?: number
  offset?: number
}

export type GetOwnerByIdResponse = AxiosResponse<OwnerDataResponse>

export type UpdateOwnerPayload = {
  ownerId: string
  fullName: string
  // VERA-914 — ISO date string (YYYY-MM-DD); replaces the old derived-from-age field.
  birthday?: string
  gender?: string
  email: string
  phone: string
  // VERA-914 (Stage B) — Details/Address/Marketing sections.
  country?: string
  addressLine?: string
  city?: string
  state?: string
  postalCode?: string
  addressCountry?: string
  // VERA-914 — geocoding from the AddressField autocomplete (VRG-99 scan map).
  latitude?: number | null
  longitude?: number | null
  placeId?: string | null
  formattedAddress?: string | null
  emailMarketingConsent?: boolean
  smsMarketingConsent?: boolean
  inviteOwner?: boolean
}

export type UpdateOwnerResponse = {
  id: string
  fullName: string
  birthday?: string | null
  gender?: string
  email: string
  phone: string
  status: string
  registrationDate: string
  invitationQueued?: boolean
  warnings?: string[]
}

export enum ActionType {
  GET_OWNERS = 'GET_OWNERS',
  SET_OWNERS = 'SET_OWNERS',
  GET_OWNER = 'GET_OWNER',
  SET_OWNER = 'SET_OWNER',
  UPDATE_OWNER = 'UPDATE_OWNER',
  DELETE_OWNER = 'DELETE_OWNER',
  BULK_DELETE_OWNERS = 'BULK_DELETE_OWNERS',
  IMPORT_SHOPIFY_OWNERS = 'IMPORT_SHOPIFY_OWNERS',
  ARCHIVE_OWNER = 'ARCHIVE_OWNER',
  UNARCHIVE_OWNER = 'UNARCHIVE_OWNER',
}

// Delete types
export type DeleteOwnerPayload = {
  ownerId: string
}

export type DeleteOwnerResponse = {
  id: string
  isDeleted: boolean
  deletedAt: string
  deletedBy: string
  alreadyDeleted: boolean
}

export type BulkDeleteOwnersPayload = {
  ownerIds: string[]
}

export type BulkDeleteOwnerResult = {
  id: string
  status: 'deleted' | 'already_deleted' | 'failed'
  alreadyDeleted: boolean
  deletedAt?: string
  code?: string
  message?: string
}

export type BulkDeleteOwnersResponse = {
  results: BulkDeleteOwnerResult[]
  summary: {
    requested: number
    deleted: number
    alreadyDeleted: number
    failed: number
  }
}

export type GetOwnersAction = Action<GetOwnersPayload>
export type SetOwnersAction = Action<Owners | null>
export type GetOwnerAction = Action<GetOwnerByIdPayload>
export type SetOwnerAction = Action<OwnerDataResponse | null>
export type UpdateOwnerAction = Action<UpdateOwnerPayload>
export type DeleteOwnerAction = Action<DeleteOwnerPayload>
export type BulkDeleteOwnersAction = Action<BulkDeleteOwnersPayload>

// Shopify import types
export type ImportShopifyOwnersPayload = {
  limit?: number
  offset?: number
  createForExisting?: boolean
}

export type ImportShopifyOwnersResponse = {
  importedCount: number
  existingCount: number
  failedCount: number
  total: number
  hasMore: boolean
  nextOffset?: number
  errors: string[]
}

export type ImportShopifyOwnersAction = Action<ImportShopifyOwnersPayload>

// Archive types
export type ArchiveOwnerPayload = {
  ownerId: string
}

export type ArchiveOwnerResponse = {
  id: string
  archived: boolean
  archived_at: string | null
}

export type ArchiveOwnerAction = Action<ArchiveOwnerPayload>
